import { useNavigate, useParams } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { BlogPost } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ViewOnStorefrontButton } from '@/features/stores';
import { useStore } from '@/shared/store/store-context';

import {
  emptyBlogPostForm,
  fromBlogPost,
  toBlogPostCreate,
  toBlogPostUpdate,
} from '../api/mappers';
import { BlogPostForm } from '../components/blog-post-form';
import { DeleteBlogPostDialog } from '../components/delete-blog-post-dialog';
import { PostStateBadge } from '../components/post-state-badge';
import { useBlogPost } from '../hooks/use-blog-post';
import { useCreateBlogPost, useUpdateBlogPost } from '../hooks/use-blog-post-mutations';
import { postState } from '../schemas/post-state';

const EDIT_ROUTE = '/_authed/_active/s/$storeId/content/blog/$postId' as const;

function BlogPostCreate() {
  const { t } = useTranslation('blog');
  const store = useStore();
  const format = useFormat();
  const navigate = useNavigate();
  const create = useCreateBlogPost(store.id);

  return (
    <PageContent>
      <PageHeader
        title={t('create.title')}
        backTo={{ to: '/s/$storeId/content/blog', params: { storeId: store.id } }}
        backLabel={t('posts.title')}
      />
      <BlogPostForm
        storeId={store.id}
        mode="create"
        post={null}
        defaultValues={emptyBlogPostForm()}
        isSubmitting={create.isPending}
        onSubmit={async (values) => {
          const created = await create.mutateAsync(toBlogPostCreate(values, format.timeZone));
          await navigate({
            to: '/s/$storeId/content/blog/$postId',
            params: { storeId: store.id, postId: created.id },
            replace: true,
          });
        }}
      />
    </PageContent>
  );
}

function DangerZone({ storeId, row }: { storeId: string; row: BlogPost }) {
  const { t } = useTranslation('blog');
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<BlogPost | null>(null);
  return (
    <FormSection title={t('danger.title')} className="border-destructive/30">
      <p className="text-muted-foreground text-sm">{t('danger.description')}</p>
      <Button
        type="button"
        variant="outline"
        className="text-destructive hover:text-destructive w-full"
        onClick={() => setDeleting(row)}
      >
        <Trash2 aria-hidden />
        {t('actions.delete')}
      </Button>
      <DeleteBlogPostDialog
        storeId={storeId}
        row={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        onDeleted={() => {
          // The record is gone; a dirty form must not keep us on its page.
          releaseSaveBar();
          void navigate({ to: '/s/$storeId/content/blog', params: { storeId } });
        }}
      />
    </FormSection>
  );
}

function BlogPostEdit() {
  const { t } = useTranslation('blog');
  const format = useFormat();
  const store = useStore();
  const { postId } = useParams({ from: EDIT_ROUTE });
  // Prefetched by the route loader, so this resolves from cache.
  const { data: post } = useBlogPost(store.id, postId);
  const update = useUpdateBlogPost(store.id);
  if (!post) return null;
  const defaults = fromBlogPost(post, format.timeZone);

  return (
    <PageContent>
      <PageHeader
        title={post.title}
        meta={<PostStateBadge state={postState(post)} publishedAt={post.publishedAt} />}
        description={
          <span title={format.utc(post.updatedAt)}>
            {t('updatedAt', { when: format.relative(post.updatedAt) })}
            {' · '}
            <code>/blog/{post.handle}</code>
          </span>
        }
        backTo={{ to: '/s/$storeId/content/blog', params: { storeId: store.id } }}
        backLabel={t('posts.title')}
        actions={<ViewOnStorefrontButton url={post.storefrontUrl} live={post.live} />}
      />
      <BlogPostForm
        key={post.updatedAt}
        storeId={store.id}
        mode="edit"
        post={post}
        defaultValues={defaults}
        isSubmitting={update.isPending}
        onSubmit={(values) =>
          update.mutateAsync({
            id: post.id,
            body: toBlogPostUpdate(values, defaults, format.timeZone),
          })
        }
        dangerZone={
          <Can permission="content.write">
            <DangerZone storeId={store.id} row={post} />
          </Can>
        }
      />
    </PageContent>
  );
}

export function BlogPostCreatePage() {
  return (
    <FeatureErrorBoundary name="blog.create">
      <BlogPostCreate />
    </FeatureErrorBoundary>
  );
}

export function BlogPostEditPage() {
  return (
    <FeatureErrorBoundary name="blog.edit">
      <BlogPostEdit />
    </FeatureErrorBoundary>
  );
}
